/**
 * Helpers puros del portal del partner (RES-93).
 *
 * Los números (comisión, MRR, escalones) los calcula el backend en `GET /partners/me` con el
 * mismo motor que usa el panel interno; acá solo se formatean y se valida el formulario de
 * datos de cobro antes de mandarlo (el backend vuelve a validar y es la fuente de verdad).
 */

/** Piso de pago mensual: debajo de este monto la comisión se arrastra al mes siguiente. */
export const PAYOUT_THRESHOLD = 10000;

export const TAX_CONDITIONS = ['monotributo', 'responsable_inscripto'];

const CBU_RE = /^\d{22}$/;
const ALIAS_RE = /^[A-Za-z0-9.-]{6,20}$/;

/**
 * 'YYYY-MM' → 'MM/YYYY'. Devuelve '' si no vino y el valor tal cual si no tiene ese formato.
 * @param {string} period período
 * @returns {string} período legible
 */
export function formatPeriod(period) {
  if (!period) return '';
  const match = /^(\d{4})-(\d{2})$/.exec(String(period));
  return match ? `${match[2]}/${match[1]}` : String(period);
}

/**
 * Fecha ISO → fecha corta en el locale dado. '' si no vino o no es válida.
 * @param {string} iso fecha
 * @param {string} locale locale BCP 47
 * @returns {string} fecha legible
 */
export function formatShortDate(iso, locale = 'es-AR') {
  if (!iso) return '';
  // 'YYYY-MM-DD' sin hora: se arma como fecha local para que no se corra un día por el huso
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso));
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/**
 * Desde cuándo el local está en su etapa actual.
 * @param {object} conversion fila de `metrics.conversions`
 * @returns {string|null} fecha ISO
 */
export function conversionSince(conversion) {
  if (!conversion) return null;
  switch (conversion.stage) {
    case 'paying':
      return conversion.firstPaidAt || conversion.attributedAt || null;
    case 'cancelled':
    case 'clawed_back':
    case 'lost':
      return conversion.cancelledAt || conversion.attributedAt || null;
    default:
      return conversion.attributedAt || null;
  }
}

/**
 * Factura que corresponde según la condición fiscal: C (monotributo) o A (responsable inscripto).
 * @param {string} taxCondition condición
 * @returns {'A'|'C'|null} tipo de factura
 */
export function invoiceTypeFor(taxCondition) {
  if (taxCondition === 'monotributo') return 'C';
  if (taxCondition === 'responsable_inscripto') return 'A';
  return null;
}

/**
 * Dígito verificador de un CUIT/CUIL (mismo algoritmo que el backend).
 * @param {string} cuit 11 dígitos
 * @returns {boolean} válido
 */
export function isValidCuit(cuit) {
  if (!/^\d{11}$/.test(cuit)) return false;
  const weights = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
  const sum = weights.reduce((acc, w, i) => acc + w * Number(cuit[i]), 0);
  const mod = 11 - (sum % 11);
  const check = mod === 11 ? 0 : mod === 10 ? 9 : mod;
  return check === Number(cuit[10]);
}

/**
 * Valores iniciales del formulario de datos de cobro.
 * @param {object|null} payoutDetails datos guardados
 * @returns {object} formulario
 */
export function initialPayoutForm(payoutDetails) {
  return {
    holderName: payoutDetails?.holderName || '',
    cuit: payoutDetails?.cuit || '',
    cbuOrAlias: payoutDetails?.cbuOrAlias || '',
    taxCondition: payoutDetails?.taxCondition || '',
  };
}

/**
 * Payload normalizado para `updateMyPayoutDetails`.
 * @param {object} form formulario
 * @returns {object} payload
 */
export function buildPayoutPayload(form) {
  return {
    holderName: String(form.holderName || '').trim(),
    cuit: String(form.cuit || '').replace(/\D/g, ''),
    cbuOrAlias: String(form.cbuOrAlias || '').trim(),
    taxCondition: form.taxCondition,
  };
}

/**
 * Valida los datos de cobro. Devuelve el primer campo inválido o null.
 * @param {object} form formulario
 * @returns {string|null} campo inválido
 */
export function validatePayoutForm(form) {
  const payload = buildPayoutPayload(form);
  if (!payload.holderName || payload.holderName.length > 120) return 'holderName';
  if (!isValidCuit(payload.cuit)) return 'cuit';
  if (!CBU_RE.test(payload.cbuOrAlias) && !ALIAS_RE.test(payload.cbuOrAlias)) return 'cbuOrAlias';
  if (!TAX_CONDITIONS.includes(payload.taxCondition)) return 'taxCondition';
  return null;
}

/**
 * Campo marcado por el backend en un 400 con el código esperado.
 * @param {*} err error de axios
 * @param {string} code código esperado
 * @returns {string|null} campo
 */
export function getApiErrorField(err, code) {
  const data = err?.response?.data;
  if (err?.response?.status === 400 && data?.code === code && data.field) return data.field;
  return null;
}

/**
 * 403 `NOT_A_PARTNER`: el usuario tiene rol PARTNER pero no un código asociado.
 * @param {*} err error de axios
 * @returns {boolean} es ese caso
 */
export function isNotAPartnerError(err) {
  return err?.response?.status === 403 && err?.response?.data?.code === 'NOT_A_PARTNER';
}

/**
 * Escala CM con rangos y el escalón actual marcado.
 * @param {Array<{minLocations: number, pct: number}>} cmTiers escala
 * @param {number} activeLocations locales activos pagos
 * @returns {Array<{minLocations: number, maxLocations: number|null, pct: number, current: boolean}>}
 */
export function buildCmTierRows(cmTiers, activeLocations = 0) {
  const sorted = [...(cmTiers || [])].sort((a, b) => a.minLocations - b.minLocations);
  const currentIndex = sorted.reduce(
    (acc, tier, i) => (activeLocations >= tier.minLocations ? i : acc),
    sorted.length ? 0 : -1
  );
  return sorted.map((tier, i) => ({
    minLocations: tier.minLocations,
    maxLocations: i < sorted.length - 1 ? sorted[i + 1].minLocations - 1 : null,
    pct: tier.pct,
    current: i === currentIndex,
  }));
}

/**
 * Bonos por alta usados (solo influencer con bono): `{ used, cap }` o null.
 * @param {object} partner partner
 * @param {number} cap tope de bonos
 * @returns {{used: number, cap: number}|null} resumen
 */
export function signOnSummary(partner, cap = 3) {
  if (!partner || partner.program !== 'influencer' || !Number(partner.signOnBonus)) return null;
  return { used: Math.min(Number(partner.signOnUsed) || 0, cap), cap };
}

/**
 * Nombre del PNG del QR.
 * @param {string} code código del partner
 * @returns {string} nombre de archivo
 */
export function qrFilename(code) {
  const safe = String(code || 'partner').replace(/[^A-Za-z0-9_-]/g, '');
  return `resto-qr-${safe || 'partner'}.png`;
}
